import clsx from "clsx"
import { useMemo } from "react"
import { ASSET_PREFIX } from "@/constants/routes"
import { useApp } from "@/context/AppContext"
import BlurredDecorationItem from "./blurred-decoration-item"

// Seeded PRNG to avoid hydration mismatches
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

const sources = [
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-01.jpg`, width: 560, height: 412 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-02.jpg`, width: 496, height: 396 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-03.jpg`, width: 492, height: 392 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-04.jpg`, width: 480, height: 380 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-05.jpg`, width: 496, height: 396 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-06.jpg`, width: 496, height: 380 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-07.jpg`, width: 372, height: 480 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-08.jpg`, width: 420, height: 492 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-09.jpg`, width: 396, height: 496 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-10.jpg`, width: 404, height: 504 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-11.jpg`, width: 382, height: 500 },
  { src: `${ASSET_PREFIX}/img/blurred-decorations/blurred-decoration-12.jpg`, width: 380, height: 480 },
]

export default function BlurredDecorations({ isFullPage = true, className, count, categoryId = '', startIndex = 0 }) {
  const { windowSize } = useApp()
  const decorations = useMemo(() => {
    const xRanges = [
      { min: -10, max: 10 },
      { min: 40, max: 60 },
      { min: 80, max: 100 }
    ]

    // Base seed from categoryId (convert string to number via hash)
    let hash = 0
    for (let i = 0; i < categoryId.length; i++) {
      hash = ((hash << 5) - hash) + categoryId.charCodeAt(i)
      hash |= 0
    }
    const baseSeed = Math.abs(hash)

    // // Pre-compute all xRangeIndex values to avoid mutation during render
    // const xRangeIndices = []
    // let prevIndex = -1
    // for (let i = 0; i < count; i++) {
    //   const availableRanges = xRanges.map((_, j) => j).filter(j => j !== prevIndex)
    //   const idx = availableRanges[Math.floor(seededRandom(baseSeed + i * 4) * availableRanges.length)]
    //   xRangeIndices.push(idx)
    //   prevIndex = idx
    // }

    return Array.from({ length: count }, (_, index) => {
      // Start from startIndex prop, then follow sequence with wrap-around
      const imageIndex = Math.floor(seededRandom(baseSeed + baseSeed * index) * sources.length);
      const image = sources[imageIndex]

      let rangeIndex = index % 3;
      if (rangeIndex === 1) {
        rangeIndex = 2
      } else if (rangeIndex === 2) {
        rangeIndex = 1
      }

      const xRange = xRanges[rangeIndex]
      const x = xRange.min + seededRandom(baseSeed + index * 4 + 2) * (xRange.max - xRange.min)

      // Distribute uniformly in y with random offset
      const baseY = (index / count) * 100
      // const yOffset = Math.random() * 5;
      const yOffset = 0;
      const y = baseY + yOffset;

      // Calculate actual dimensions (divide by 2 since images are 2x)
      const actualWidth = windowSize.width >= 1440 ? image.width / 2 : image.width / 3;
      const actualHeight = windowSize.width >= 1440 ? image.height / 2 : image.height / 3;
      // Convert width to rem
      const widthInRem = actualWidth / 16

      // Weight for parallax effect (between 0.5 and 1.5)
      const weight = 0.5 + seededRandom(index * 3 + 2)

      return {
        src: image.src,
        width: image.width,
        height: image.height,
        actualWidth,
        actualHeight,
        widthInRem,
        x,
        y,
        weight
      }
    })
  }, [count, categoryId, startIndex, windowSize])

  return (
    <div className={clsx(
      "w-screen lg:w-full absolute max-lg:translate-x-[-50%] left-[50%] lg:left-0 pointer-events-none",
      isFullPage ? 'h-full top-0' : 'h-[calc(100%-100vh)] bottom-0',
      className
    )}>
      {decorations.map((decoration, index) => (
        <BlurredDecorationItem
          key={index}
          src={decoration.src}
          width={decoration.width}
          height={decoration.height}
          widthInRem={decoration.widthInRem}
          left={decoration.x}
          top={decoration.y}
          weight={decoration.weight}
        />
      ))}
    </div>
  )
}